<template>
  <div class="movimientos-internos-container">
    <!-- Header -->
    <v-card>
      <v-card-title class="d-flex flex-column flex-md-row justify-space-between align-start align-md-center">
        <div class="mb-3 mb-md-0">
          <span>Movimientos Internos</span>
          <div class="text-caption text-grey mt-1">
            Transferencias entre ubicaciones, reubicaciones y consolidaciones
          </div>
        </div>
        <div class="d-flex flex-column flex-sm-row ga-2 ga-sm-4 align-start align-sm-center">
          <v-btn
            color="primary"
            prepend-icon="mdi-swap-horizontal"
            @click="showNewMovementModal"
            size="default"
            class="d-none d-sm-flex"
          >
            Nuevo Movimiento
          </v-btn>
          <v-btn
            color="primary"
            icon="mdi-swap-horizontal"
            @click="showNewMovementModal"
            size="small"
            class="d-sm-none"
          ></v-btn>
          <v-btn
            color="info"
            prepend-icon="mdi-package-variant-closed"
            @click="showConsolidationModal"
            size="default"
            class="d-none d-sm-flex"
          >
            Consolidar Ubicaciones
          </v-btn>
          <v-btn
            color="info"
            icon="mdi-package-variant-closed"
            @click="showConsolidationModal"
            size="small"
            class="d-sm-none"
          ></v-btn>
          <v-btn
            color="success"
            prepend-icon="mdi-microsoft-excel"
            @click="exportMovements"
            :loading="exporting"
            size="default"
            class="d-none d-sm-flex"
          >
            Exportar
          </v-btn>
          <v-btn
            color="success"
            icon="mdi-microsoft-excel"
            @click="exportMovements"
            :loading="exporting"
            size="small"
            class="d-sm-none"
          ></v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <!-- Filtros -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Estado"
              hide-details
              density="compact"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="filters.type"
              :items="movementTypes"
              label="Tipo de Movimiento"
              hide-details
              density="compact"
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.dateFrom"
              label="Fecha desde"
              type="date"
              hide-details
              density="compact"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar"
              placeholder="Referencia, producto..."
              hide-details
              density="compact"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabla de movimientos -->
        <ResponsiveDataTable
          :items="filteredMovements"
          :headers="headers"
          :loading="loading"
          :search="filters.search"
          class="elevation-1"
          :show-select="false"
          items-per-page="15"
          density="default"
          hover
        >
          <template v-slot:item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:item.type="{ item }">
            <v-chip
              :color="getTypeColor(item.movement_type)"
              size="small"
            >
              {{ getTypeLabel(item.movement_type) }}
            </v-chip>
          </template>

          <template v-slot:item.products_count="{ item }">
            <div class="text-center">
              <v-chip size="small" color="info">
                {{ item.details?.length || 0 }} productos
              </v-chip>
            </div>
          </template>

          <template v-slot:item.source_location="{ item }">
            <div v-if="item.details && item.details.length > 0">
              {{ getLocationName(item.details[0].source_location) }}
            </div>
            <span v-else class="text-grey">N/A</span>
          </template>

          <template v-slot:item.destination_location="{ item }">
            <div v-if="item.details && item.details.length > 0">
              {{ getLocationName(item.details[0].destination_location) }}
            </div>
            <span v-else class="text-grey">N/A</span>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              size="small"
            >
              {{ item.status }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              color="info"
              @click="viewMovement(item)"
            ></v-btn>
            <v-btn
              v-if="item.status === 'Pendiente'"
              icon="mdi-check"
              size="small"
              variant="text"
              color="success"
              @click="executeMovement(item)"
              title="Ejecutar Movimiento"
            ></v-btn>
            <v-btn
              v-if="item.status === 'Pendiente'"
              icon="mdi-pencil"
              size="small"
              variant="text"
              color="primary"
              @click="editMovement(item)"
            ></v-btn>
            <v-btn
              v-if="item.status === 'Pendiente'"
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="deleteMovement(item)"
            ></v-btn>
          </template>

          <template v-slot:no-data>
            <div class="text-center py-4">
              <v-icon icon="mdi-swap-horizontal" color="grey" size="large"></v-icon>
              <p class="text-subtitle-1 mt-2">No se encontraron movimientos</p>
            </div>
          </template>
        </ResponsiveDataTable>
      </v-card-text>
    </v-card>

    <!-- Modal de Nuevo/Editar Movimiento -->
    <v-dialog v-model="showMovementDialog" max-width="1200" persistent>
      <v-card>
        <v-card-title>
          {{ isEditingMovement ? 'Editar Movimiento' : 'Nuevo Movimiento Interno' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="movementForm" @submit.prevent="saveMovement">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="movementForm.movement_type"
                  :items="movementTypes"
                  label="Tipo de Movimiento"
                  :rules="[v => !!v || 'El tipo es requerido']"
                  required
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="movementForm.notes"
                  label="Notas/Motivo"
                  placeholder="Ej: Optimización de espacio, reubicación..."
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Productos a mover -->
            <v-divider class="my-4"></v-divider>
            <div class="d-flex justify-space-between align-center mb-4">
              <h3>Productos a Mover</h3>
              <v-btn
                color="primary"
                variant="outlined"
                prepend-icon="mdi-plus"
                @click="addProductToMovement"
              >
                Agregar Producto
              </v-btn>
            </div>

            <ResponsiveDataTable
              :items="movementForm.items"
              :headers="itemHeaders"
              class="elevation-1 mb-4"
              density="compact"
              :show-select="false"
            >
              <template v-slot:item.product="{ item, index }">
                <v-autocomplete
                  v-model="item.product_id"
                  :items="availableProducts"
                  item-title="display_name"
                  item-value="id"
                  label="Producto"
                  density="compact"
                  hide-details
                  @update:model-value="updateProductSelection(index, $event)"
                >
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.raw.sku }}</v-list-item-subtitle>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </template>

              <template v-slot:item.source_location="{ item, index }">
                <v-select
                  v-model="item.source_location_id"
                  :items="getProductLocations(item.product_id)"
                  item-title="display_name"
                  item-value="id"
                  label="Ubicación Origen"
                  density="compact"
                  hide-details
                  @update:model-value="updateSourceLocation(index, $event)"
                ></v-select>
              </template>

              <template v-slot:item.available_quantity="{ item }">
                <div class="text-center">
                  <v-chip size="small" color="info">
                    {{ item.available_quantity || 0 }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:item.quantity="{ item }">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  min="1"
                  :max="item.available_quantity"
                  density="compact"
                  hide-details
                ></v-text-field>
              </template>

              <template v-slot:item.destination_location="{ item, index }">
                <v-select
                  v-model="item.destination_location_id"
                  :items="availableDestinations"
                  item-title="display_name"
                  item-value="id"
                  label="Ubicación Destino"
                  density="compact"
                  hide-details
                ></v-select>
              </template>

              <template v-slot:item.actions="{ item, index }">
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  variant="text"
                  color="error"
                  @click="removeProductFromMovement(index)"
                ></v-btn>
              </template>
            </ResponsiveDataTable>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showMovementDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="saveMovement"
            :loading="submitting"
            :disabled="!isMovementFormValid"
          >
            {{ isEditingMovement ? 'Actualizar' : 'Crear' }} Movimiento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Consolidación -->
    <v-dialog v-model="showConsolidationDialog" max-width="800">
      <v-card>
        <v-card-title>Consolidar Ubicaciones</v-card-title>
        <v-card-text>
          <v-alert type="info" class="mb-4">
            Selecciona un producto para consolidar todas sus ubicaciones fragmentadas en una sola ubicación.
          </v-alert>
          
          <v-autocomplete
            v-model="consolidationForm.product_id"
            :items="productsWithMultipleLocations"
            item-title="display_name"
            item-value="id"
            label="Producto a Consolidar"
            @update:model-value="loadProductLocationsForConsolidation"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.raw.sku }} - {{ item.raw.locations_count }} ubicaciones
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-autocomplete>

          <div v-if="consolidationLocations.length > 0" class="mt-4">
            <h4>Ubicaciones Actuales:</h4>
            <v-list>
              <v-list-item
                v-for="location in consolidationLocations"
                :key="location.id"
              >
                <v-list-item-title>{{ location.location_name }}</v-list-item-title>
                <v-list-item-subtitle>Cantidad: {{ location.quantity }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>

            <v-select
              v-model="consolidationForm.destination_location_id"
              :items="availableDestinations"
              item-title="display_name"
              item-value="id"
              label="Ubicación Destino para Consolidación"
              class="mt-4"
            ></v-select>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="showConsolidationDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            @click="createConsolidationMovement"
            :loading="submitting"
            :disabled="!consolidationForm.product_id || !consolidationForm.destination_location_id"
          >
            Crear Movimiento de Consolidación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de Vista de Movimiento -->
    <v-dialog v-model="showViewDialog" max-width="800">
      <v-card v-if="selectedMovement">
        <v-card-title>
          Detalle del Movimiento - {{ selectedMovement.reference }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3">
                <h4>Información General</h4>
                <div class="mt-2">
                  <strong>Tipo:</strong> {{ getTypeLabel(selectedMovement.movement_type) }}
                </div>
                <div>
                  <strong>Estado:</strong> 
                  <v-chip :color="getStatusColor(selectedMovement.status)" size="small" class="ml-2">
                    {{ selectedMovement.status }}
                  </v-chip>
                </div>
                <div>
                  <strong>Fecha:</strong> {{ formatDate(selectedMovement.created_at) }}
                </div>
                <div v-if="selectedMovement.notes">
                  <strong>Notas:</strong> {{ selectedMovement.notes }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card variant="outlined" class="pa-3">
                <h4>Resumen</h4>
                <div class="mt-2">
                  <strong>Total Productos:</strong> {{ selectedMovement.details?.length || 0 }}
                </div>
                <div>
                  <strong>Total Unidades:</strong> 
                  {{ selectedMovement.details?.reduce((sum, item) => sum + item.quantity, 0) || 0 }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>
          <h4>Productos</h4>
          <ResponsiveDataTable
            :items="selectedMovement.details || []"
            :headers="viewHeaders"
            class="elevation-1 mt-2"
            density="compact"
          >
            <template v-slot:item.product="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.product?.name }}</div>
                <div class="text-caption text-grey">{{ item.product?.sku }}</div>
              </div>
            </template>
            <template v-slot:item.source_location="{ item }">
              {{ getLocationName(item.source_location) }}
            </template>
            <template v-slot:item.destination_location="{ item }">
              {{ getLocationName(item.destination_location) }}
            </template>
          </ResponsiveDataTable>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showViewDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as XLSX from 'xlsx'
import api from '@/api'
import ResponsiveDataTable from '@/components/ResponsiveDataTable.vue'

export default {
  name: 'MovimientosInternos',
  components: {
    ResponsiveDataTable
  },
  data() {
    return {
      loading: false,
      exporting: false,
      submitting: false,
      showMovementDialog: false,
      showConsolidationDialog: false,
      showViewDialog: false,
      isEditingMovement: false,
      selectedMovement: null,
      
      filters: {
        status: '',
        type: '',
        dateFrom: '',
        search: ''
      },

      statusOptions: [
        { title: 'Pendiente', value: 'Pendiente' },
        { title: 'En Proceso', value: 'En Proceso' },
        { title: 'Completado', value: 'Completado' },
        { title: 'Cancelado', value: 'Cancelado' }
      ],

      movementTypes: [
        { title: 'Reubicación', value: 'reubicacion' },
        { title: 'Consolidación', value: 'consolidacion' },
        { title: 'Optimización', value: 'optimizacion' },
        { title: 'Transferencia entre Zonas', value: 'transferencia_zona' },
        { title: 'Mantenimiento', value: 'mantenimiento' }
      ],

      headers: [
        { title: 'Referencia', key: 'reference', align: 'start', sortable: true },
        { title: 'Fecha', key: 'created_at', align: 'start', sortable: true },
        { title: 'Tipo', key: 'type', align: 'center', sortable: true },
        { title: 'Productos', key: 'products_count', align: 'center', sortable: false },
        { title: 'Origen', key: 'source_location', align: 'start', sortable: false },
        { title: 'Destino', key: 'destination_location', align: 'start', sortable: false },
        { title: 'Estado', key: 'status', align: 'center', sortable: true },
        { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
      ],

      itemHeaders: [
        { title: 'Producto', key: 'product', align: 'start', sortable: false },
        { title: 'Origen', key: 'source_location', align: 'start', sortable: false },
        { title: 'Disponible', key: 'available_quantity', align: 'center', sortable: false },
        { title: 'Cantidad', key: 'quantity', align: 'center', sortable: false },
        { title: 'Destino', key: 'destination_location', align: 'start', sortable: false },
        { title: 'Acciones', key: 'actions', align: 'center', sortable: false }
      ],

      viewHeaders: [
        { title: 'Producto', key: 'product', align: 'start' },
        { title: 'Cantidad', key: 'quantity', align: 'center' },
        { title: 'Origen', key: 'source_location', align: 'start' },
        { title: 'Destino', key: 'destination_location', align: 'start' }
      ],

      movementForm: {
        movement_type: '',
        notes: '',
        items: []
      },

      consolidationForm: {
        product_id: null,
        destination_location_id: null
      },

      consolidationLocations: [],
      movements: []
    }
  },

  computed: {
    ...mapGetters({
      products: 'products/allProducts',
      locations: 'locations/allLocations',
      inventory: 'inventory/allInventory'
    }),

    filteredMovements() {
      let result = this.movements || []
      
      if (this.filters.status) {
        result = result.filter(movement => movement.status === this.filters.status)
      }
      
      if (this.filters.type) {
        result = result.filter(movement => movement.movement_type === this.filters.type)
      }
      
      if (this.filters.dateFrom) {
        const fromDate = new Date(this.filters.dateFrom)
        result = result.filter(movement => {
          const movementDate = new Date(movement.created_at)
          return movementDate >= fromDate
        })
      }
      
      return result
    },

    availableProducts() {
      return this.products.map(product => ({
        ...product,
        display_name: `${product.name} (${product.sku})`
      }))
    },

    availableDestinations() {
      return this.locations.map(location => ({
        ...location,
        display_name: `${location.code} - ${location.description}`
      }))
    },

    productsWithMultipleLocations() {
      const productLocationCount = {}
      
      this.inventory.forEach(item => {
        if (!productLocationCount[item.product_id]) {
          productLocationCount[item.product_id] = 0
        }
        productLocationCount[item.product_id]++
      })

      return this.products
        .filter(product => productLocationCount[product.id] > 1)
        .map(product => ({
          ...product,
          display_name: `${product.name} (${product.sku})`,
          locations_count: productLocationCount[product.id]
        }))
    },

    isMovementFormValid() {
      return this.movementForm.movement_type && 
             this.movementForm.items.length > 0 &&
             this.movementForm.items.every(item => 
               item.product_id && 
               item.source_location_id && 
               item.destination_location_id && 
               item.quantity > 0 &&
               item.quantity <= item.available_quantity
             )
    }
  },

  methods: {
    ...mapActions({
      fetchProducts: 'products/fetchProducts',
      fetchLocations: 'locations/fetchLocations',
      fetchInventory: 'inventory/fetchInventory'
    }),

    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchProducts(),
          this.fetchLocations(),
          this.fetchInventory(),
          this.loadMovements()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        this.showMessage('Error al cargar los datos', 'error')
      } finally {
        this.loading = false
      }
    },

    async loadMovements() {
      try {
        const response = await api.getMovimientosInternos()
        this.movements = response.data || []
      } catch (error) {
        console.error('Error loading movements:', error)
        this.showMessage('Error al cargar los movimientos', 'error')
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES')
    },

    getTypeColor(type) {
      const colors = {
        'reubicacion': 'primary',
        'consolidacion': 'info',
        'optimizacion': 'success',
        'transferencia_zona': 'warning',
        'mantenimiento': 'orange'
      }
      return colors[type] || 'grey'
    },

    getTypeLabel(type) {
      const labels = {
        'reubicacion': 'Reubicación',
        'consolidacion': 'Consolidación',
        'optimizacion': 'Optimización',
        'transferencia_zona': 'Transferencia entre Zonas',
        'mantenimiento': 'Mantenimiento'
      }
      return labels[type] || type
    },

    getStatusColor(status) {
      const colors = {
        'Pendiente': 'warning',
        'En Proceso': 'info',
        'Completado': 'success',
        'Cancelado': 'error'
      }
      return colors[status] || 'grey'
    },

    getLocationName(location) {
      if (!location) return 'N/A'
      return `${location.code} - ${location.description}`
    },

    getProductLocations(productId) {
      if (!productId) return []
      
      const productInventory = this.inventory.filter(item => item.product_id === productId)
      return productInventory.map(item => {
        const location = this.locations.find(loc => loc.id === item.location_id)
        return {
          id: item.location_id,
          display_name: location ? `${location.code} - ${location.description} (${item.quantity} unidades)` : `Ubicación ${item.location_id}`,
          quantity: item.quantity
        }
      })
    },

    showNewMovementModal() {
      this.isEditingMovement = false
      this.resetMovementForm()
      this.showMovementDialog = true
    },

    showConsolidationModal() {
      this.consolidationForm = {
        product_id: null,
        destination_location_id: null
      }
      this.consolidationLocations = []
      this.showConsolidationDialog = true
    },

    resetMovementForm() {
      this.movementForm = {
        movement_type: '',
        notes: '',
        items: []
      }
    },

    addProductToMovement() {
      this.movementForm.items.push({
        product_id: null,
        source_location_id: null,
        destination_location_id: null,
        quantity: 1,
        available_quantity: 0
      })
    },

    removeProductFromMovement(index) {
      this.movementForm.items.splice(index, 1)
    },

    updateProductSelection(index, productId) {
      const item = this.movementForm.items[index]
      item.product_id = productId
      item.source_location_id = null
      item.available_quantity = 0
      item.quantity = 1
    },

    updateSourceLocation(index, locationId) {
      const item = this.movementForm.items[index]
      item.source_location_id = locationId
      
      // Buscar la cantidad disponible en esa ubicación
      const inventoryItem = this.inventory.find(inv => 
        inv.product_id === item.product_id && inv.location_id === locationId
      )
      
      item.available_quantity = inventoryItem ? inventoryItem.quantity : 0
      item.quantity = Math.min(item.quantity, item.available_quantity)
    },

    async saveMovement() {
      if (!this.isMovementFormValid) return

      this.submitting = true
      try {
        const movementData = {
          movement_type: this.movementForm.movement_type,
          notes: this.movementForm.notes,
          details: this.movementForm.items.map(item => ({
            product_id: item.product_id,
            source_location_id: item.source_location_id,
            destination_location_id: item.destination_location_id,
            quantity: item.quantity
          }))
        }

        if (this.isEditingMovement) {
          await api.updateMovimientoInterno(this.selectedMovement.id, movementData)
          this.showMessage('Movimiento actualizado correctamente', 'success')
        } else {
          await api.createMovimientoInterno(movementData)
          this.showMessage('Movimiento creado correctamente', 'success')
        }
        
        this.showMovementDialog = false
        this.resetMovementForm()
        await this.loadMovements()
      } catch (error) {
        console.error('Error saving movement:', error)
        this.showMessage('Error al guardar el movimiento', 'error')
      } finally {
        this.submitting = false
      }
    },

    loadProductLocationsForConsolidation(productId) {
      if (!productId) {
        this.consolidationLocations = []
        return
      }

      this.consolidationLocations = this.inventory
        .filter(item => item.product_id === productId)
        .map(item => {
          const location = this.locations.find(loc => loc.id === item.location_id)
          return {
            id: item.location_id,
            location_name: location ? `${location.code} - ${location.description}` : `Ubicación ${item.location_id}`,
            quantity: item.quantity
          }
        })
    },

    async createConsolidationMovement() {
      if (!this.consolidationForm.product_id || !this.consolidationForm.destination_location_id) return

      this.submitting = true
      try {
        const consolidationData = {
          movement_type: 'consolidacion',
          notes: 'Consolidación automática de ubicaciones',
          details: this.consolidationLocations
            .filter(loc => loc.id !== this.consolidationForm.destination_location_id)
            .map(loc => ({
              product_id: this.consolidationForm.product_id,
              source_location_id: loc.id,
              destination_location_id: this.consolidationForm.destination_location_id,
              quantity: loc.quantity
            }))
        }

        await api.createMovimientoInterno(consolidationData)
        
        this.showConsolidationDialog = false
        await this.loadMovements()
        
        this.showMessage('Movimiento de consolidación creado correctamente', 'success')
      } catch (error) {
        console.error('Error creating consolidation movement:', error)
        this.showMessage('Error al crear el movimiento de consolidación', 'error')
      } finally {
        this.submitting = false
      }
    },

    viewMovement(movement) {
      this.selectedMovement = movement
      this.showViewDialog = true
    },

    editMovement(movement) {
      this.isEditingMovement = true
      this.selectedMovement = movement
      
      // Cargar datos del movimiento en el formulario
      this.movementForm = {
        movement_type: movement.movement_type || 'reubicacion',
        notes: movement.notes ? movement.notes.split(':').slice(1).join(':').trim() : '',
        items: movement.details.map(detail => ({
          product_id: detail.product.id,
          source_location_id: detail.source_location.id,
          destination_location_id: detail.destination_location.id,
          quantity: detail.quantity,
          available_quantity: detail.quantity // Asumimos que la cantidad actual es la disponible
        }))
      }
      
      this.showMovementDialog = true
    },

    async executeMovement(movement) {
      if (!confirm('¿Está seguro de que desea ejecutar este movimiento? Esta acción actualizará el inventario.')) {
        return
      }

      try {
        await api.executeMovimientoInterno(movement.id)
        await this.loadMovements()
        this.showMessage('Movimiento ejecutado correctamente', 'success')
      } catch (error) {
        console.error('Error executing movement:', error)
        this.showMessage('Error al ejecutar el movimiento', 'error')
      }
    },

    async deleteMovement(movement) {
      if (!confirm('¿Está seguro de que desea eliminar este movimiento?')) {
        return
      }

      try {
        await api.deleteMovimientoInterno(movement.id)
        await this.loadMovements()
        this.showMessage('Movimiento eliminado correctamente', 'success')
      } catch (error) {
        console.error('Error deleting movement:', error)
        this.showMessage('Error al eliminar el movimiento', 'error')
      }
    },

    async exportMovements() {
      this.exporting = true
      try {
        const data = this.filteredMovements.map(movement => ({
          Referencia: movement.reference,
          Fecha: this.formatDate(movement.created_at),
          Tipo: this.getTypeLabel(movement.movement_type),
          Estado: movement.status,
          Productos: movement.details?.length || 0,
          Notas: movement.notes || ''
        }))

        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos Internos')
        
        const fileName = `movimientos_internos_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(workbook, fileName)
        
        this.showMessage('Datos exportados correctamente', 'success')
      } catch (error) {
        console.error('Error exporting data:', error)
        this.showMessage('Error al exportar los datos', 'error')
      } finally {
        this.exporting = false
      }
    },

    showMessage(message, type) {
      if (type === 'success') {
        this.$store.dispatch('showSuccess', message)
      } else if (type === 'error') {
        this.$store.dispatch('showError', message)
      }
    }
  },

  async mounted() {
    await this.loadData()
  }
}
</script>

<style scoped>
.movimientos-internos-container {
  padding: 20px;
}

.v-card {
  border-radius: 8px;
}

.v-card-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.v-data-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.v-data-table .v-data-table__thead {
  background-color: #f8f9fa !important;
}

.v-data-table .v-data-table__thead .v-data-table__th {
  background-color: #f8f9fa !important;
  font-weight: 600 !important;
  color: #495057 !important;
}

.v-data-table .v-data-table__tbody tr:hover {
  background-color: #f8f9fa !important;
}

/* Estilos responsivos */
@media (max-width: 768px) {
  .movimientos-internos-container {
    padding: 10px;
  }
  
  .v-card-title {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .v-card-title span {
    font-size: 14px;
  }
  
  .text-caption {
    font-size: 11px;
  }
  
  /* Filtros responsivos */
  .v-row .v-col {
    padding: 4px 8px;
  }
  
  /* Botones responsivos */
  .v-btn {
    font-size: 12px;
  }
  
  .v-btn--size-small {
    min-width: 32px;
    height: 32px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 10px;
    height: 20px;
  }
  
  /* Modales responsivos */
  .v-dialog .v-card {
    margin: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-dialog .v-card-text {
    padding: 12px;
  }
}

@media (max-width: 480px) {
  .movimientos-internos-container {
    padding: 8px;
  }
  
  .v-card {
    margin-bottom: 12px;
  }
  
  .v-card-title {
    padding: 8px 12px;
    font-size: 14px;
  }
  
  .v-card-title span {
    font-size: 13px;
  }
  
  .v-card-text {
    padding: 12px;
  }
  
  /* Filtros para móviles pequeños */
  .v-text-field, .v-select {
    font-size: 12px;
  }
  
  .v-field__input {
    font-size: 12px;
  }
  
  /* Chips más pequeños */
  .v-chip {
    font-size: 9px;
    height: 18px;
  }
  
  /* Botones más compactos */
  .v-btn {
    font-size: 11px;
    min-height: 28px;
  }
  
  /* Modales para móviles pequeños */
  .v-dialog .v-card {
    margin: 4px;
    border-radius: 8px;
  }
  
  .v-dialog .v-card-title {
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .v-dialog .v-card-text {
    padding: 8px;
  }
}
</style> 